import { useEffect } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { dateMatchesEventSchedule, normalizeHm, TDR, MONTHSTR_FMT } from 'tdr-common';
import { mostCompatibleGroupSize } from '../helpers/groupSIze';
import { applyGroupSizeFilter } from '../helpers/applyGroupSizeFilter';
import { getEventCalendarTables, resolveEventBookNowGroupSize } from '../helpers/resolveEventBookNowGroupSize';
import { findFirstAvailableDate } from '../helpers/getFirstAvailableDate';
import { prefetchCalendarAvailability, getCalendarAvailability } from '../api/availability/availability';
import { prefetchServiceTimeslots } from '../api/getServiceTimeslots';
import { toTableDTO } from '../api/availability/dto';
import { cacheEventBookNowFromPrefetch } from '../helpers/cacheEventBookNowFromPrefetch';

/** Console prefix — filter DevTools console by this string. */
const LOG_PREFIX = '[prefetchAvailability]';
const isDev = process.env.NODE_ENV === 'development';

function prefetchLog(...args: unknown[]): void {
	if (isDev) {
		console.info(...args);
	}
}

function elapsedMs(start: number): number {
	return Math.round(performance.now() - start);
}

function findFirstMatchingDate(
	calendarAvailability: Record<string, TDR.Availability.dayAvailability> | Map<string, TDR.Availability.dayAvailability> | undefined,
	event: TDR.Event,
	todayIso: string
): string | undefined {
	if (!calendarAvailability) {
		return undefined;
	}
	const entries = calendarAvailability instanceof Map
		? Array.from(calendarAvailability.entries())
		: Object.entries(calendarAvailability);
	const sorted = entries.sort(([a], [b]) => a.localeCompare(b));
	for (const [dateStr, avail] of sorted) {
		if (dateStr.localeCompare(todayIso) < 0) {
			continue;
		}
		if (!avail || avail.available <= 0) {
			continue;
		}
		if (dateMatchesEventSchedule(dateStr, event)) {
			return dateStr;
		}
	}
	return undefined;
}

/**
 * Imperative prefetch of availability for the default group size.
 * Idempotent — react-query dedupes by queryKey, so calling more than
 * once during a session is safe.
 */
export function prefetchAvailability(
	queryClient: QueryClient,
	restaurant: TDR.Restaurant | undefined,
	tables: TDR.Table[] | undefined
) {
	if (!restaurant || !tables?.length) {
		return;
	}

	const today = DateTime.now().setZone(restaurant.timezone);
	const groupSize = mostCompatibleGroupSize(tables);
	const filteredTables = applyGroupSizeFilter(tables, groupSize);
	const tableIds = filteredTables.map(t => t.id);
	const currentMonth = today.toFormat(MONTHSTR_FMT);

	const prefetchSequence = async () => {
		await prefetchCalendarAvailability(queryClient, {
			tables: filteredTables,
			month: currentMonth,
			restaurantId: restaurant.id
		});

		const calendarData = await getCalendarAvailability({
			tables: toTableDTO(filteredTables),
			month: currentMonth,
			restaurantId: restaurant.id
		});

		const firstAvailableDate = findFirstAvailableDate(calendarData?.calendarAvailability);

		if (firstAvailableDate) {
			await prefetchServiceTimeslots(queryClient, {
				tableIds,
				dateStr: firstAvailableDate,
				restaurantId: restaurant.id
			});
		}
	};

	prefetchSequence().catch(err => {
		console.warn('Prefetch availability failed:', err);
	});
}

/**
 * Prefetch calendar + timeslots for one event.
 *
 * Months are fetched sequentially and we stop as soon as a matching date is
 * found — most events resolve in the current month, so months 2 and 3 are
 * rarely needed. prefetchQuery is a no-op when data is already fresh in cache.
 */
async function prefetchEventAvailability(
	queryClient: QueryClient,
	event: TDR.Event,
	tables: TDR.Table[],
	restaurantId: string,
	restaurantSlug: string,
	monthKeys: string[],
	todayIso: string
): Promise<void> {
	const eventStart = performance.now();
	const evKind: 'table' | 'param' = event.eventType === 'table' ? 'table' : 'param';
	const linkedTable = evKind === 'table' && event.linkedTableSlug
		? tables.find((t) => t.slug === event.linkedTableSlug)
		: undefined;

	const calendarTables = getEventCalendarTables(event, tables, linkedTable);
	if (!calendarTables.length) {
		// Do not cache negative — useEventAvailability will resolve live.
		prefetchLog(`${LOG_PREFIX} event skipped (no calendar tables)`, {
			eventId: event.id,
			eventName: event.name,
			evKind,
			linkedTableSlug: event.linkedTableSlug ?? null,
			totalMs: elapsedMs(eventStart)
		});
		return;
	}

	const betweenStart = normalizeHm(event.condition?.value?.betweenTimes?.[0]);
	const eventGroupSize = resolveEventBookNowGroupSize(event, tables, linkedTable);

	// Fetch one month at a time; stop as soon as a matching date is found
	let firstDate: string | undefined;
	let monthsFetched = 0;
	for (const month of monthKeys) {
		const monthStart = performance.now();
		const cached = await prefetchCalendarAvailability(queryClient, {
			tables: calendarTables,
			month,
			restaurantId
		});
		monthsFetched++;

		const cal = cached?.calendarAvailability;
		if (!cal) {
			console.warn(`${LOG_PREFIX} event calendar cache miss after prefetch`, {
				eventId: event.id,
				eventName: event.name,
				month
			});
		}
		prefetchLog(`${LOG_PREFIX} event calendar month`, {
			eventId: event.id,
			eventName: event.name,
			month,
			calendarPrefetchMs: elapsedMs(monthStart)
		});
		if (cal) {
			firstDate = findFirstMatchingDate(cal, event, todayIso);
			if (firstDate) {
				break;
			}
		}
	}

	if (!firstDate) {
		prefetchLog(`${LOG_PREFIX} event done (no matching date)`, {
			eventId: event.id,
			eventName: event.name,
			monthsFetched,
			totalMs: elapsedMs(eventStart)
		});
		return;
	}

	// Prefetch timeslots for the first matching date
	const slotsStart = performance.now();
	let didPrefetchSlots = false;
	if (evKind === 'table' && linkedTable?.id) {
		didPrefetchSlots = true;
		await prefetchServiceTimeslots(queryClient, {
			tableIds: [linkedTable.id],
			dateStr: firstDate,
			restaurantId,
			options: { groupSize: eventGroupSize }
		}).catch(() => null);
	}
	else if (evKind === 'param' && betweenStart !== undefined) {
		didPrefetchSlots = true;
		await prefetchServiceTimeslots(queryClient, {
			tableIds: calendarTables.map((t) => t.id),
			dateStr: firstDate,
			restaurantId,
			options: { groupSize: eventGroupSize }
		}).catch(() => null);
	}

	cacheEventBookNowFromPrefetch(queryClient, event, tables, restaurantId, restaurantSlug, firstDate);

	prefetchLog(`${LOG_PREFIX} event done`, {
		eventId: event.id,
		eventName: event.name,
		firstDate,
		monthsFetched,
		...(didPrefetchSlots ? { slotsPrefetchMs: elapsedMs(slotsStart) } : {}),
		totalMs: elapsedMs(eventStart)
	});
}

/**
 * Proactively prefetch availability data AND compute first available slot.
 *
 * Prefetches:
 * 1. General calendar + timeslots for /explore defaults (unchanged).
 * 2. Per-event calendar + timeslots for all active events, so EventCard
 *    Book Now buttons are ready before the user reaches the carousel.
 *
 * React Query's prefetchQuery skips the network call when data is already
 * fresh in cache, so re-running this hook is cheap.
 */
export function usePrefetchAvailability(
	restaurant: TDR.Restaurant | undefined,
	tables: TDR.Table[] | undefined,
	events?: TDR.Event[]
) {
	const queryClient = useQueryClient();

	useEffect(() => {
		if (!restaurant || !tables?.length) {
			return;
		}

		prefetchLog(`${LOG_PREFIX} hook effect scheduled`, {
			restaurantId: restaurant.id,
			tableCount: tables.length,
			eventCount: events?.length ?? 0,
			at: new Date().toISOString()
		});

		const today = DateTime.now().setZone(restaurant.timezone);
		const groupSize = mostCompatibleGroupSize(tables);
		const filteredTables = applyGroupSizeFilter(tables, groupSize);
		const tableIds = filteredTables.map(t => t.id);
		const todayIso = today.toISODate() ?? '';

		const monthKeys = [0, 1, 2].map((i) =>
			today.startOf('month').plus({ months: i }).toFormat(MONTHSTR_FMT)
		);
		const currentMonth = monthKeys[0];

		const prefetchSequence = async () => {
			const seqStart = performance.now();
			prefetchLog(`${LOG_PREFIX} sequence start`, {
				restaurantId: restaurant.id,
				currentMonth,
				at: new Date().toISOString()
			});

			// ── 1. General availability for /explore defaults ──────────────
			const genCalStart = performance.now();
			await prefetchCalendarAvailability(queryClient, {
				tables: filteredTables,
				month: currentMonth,
				restaurantId: restaurant.id
			});
			prefetchLog(`${LOG_PREFIX} general calendar prefetch`, {
				ms: elapsedMs(genCalStart),
				month: currentMonth
			});

			const genFetchStart = performance.now();
			const calendarData = await getCalendarAvailability({
				tables: toTableDTO(filteredTables),
				month: currentMonth,
				restaurantId: restaurant.id
			});
			prefetchLog(`${LOG_PREFIX} general getCalendarAvailability`, { ms: elapsedMs(genFetchStart) });

			const firstAvailableDate = findFirstAvailableDate(calendarData?.calendarAvailability);

			if (firstAvailableDate) {
				const slotsStart = performance.now();
				await prefetchServiceTimeslots(queryClient, {
					tableIds,
					dateStr: firstAvailableDate,
					restaurantId: restaurant.id
				});
				prefetchLog(`${LOG_PREFIX} general timeslots prefetch`, {
					ms: elapsedMs(slotsStart),
					dateStr: firstAvailableDate
				});
			}
			else {
				prefetchLog(`${LOG_PREFIX} general timeslots skipped (no first date)`);
			}

			// ── 2. Per-event availability for event cards ──────────────────
			if (!events?.length) {
				prefetchLog(`${LOG_PREFIX} sequence complete (no events)`, {
					totalMs: elapsedMs(seqStart)
				});
				return;
			}

			const eventsPhaseStart = performance.now();
			await Promise.all(
				events.map((event) =>
					prefetchEventAvailability(
						queryClient,
						event,
						tables,
						restaurant.id,
						restaurant.slug,
						monthKeys,
						todayIso
					).catch(() => null)
				)
			);
			prefetchLog(`${LOG_PREFIX} all events prefetch wave`, {
				eventCount: events.length,
				ms: elapsedMs(eventsPhaseStart)
			});

			prefetchLog(`${LOG_PREFIX} sequence complete`, {
				totalMs: elapsedMs(seqStart),
				restaurantId: restaurant.id
			});
		};

		prefetchSequence().catch(err => {
			console.warn(`${LOG_PREFIX} sequence failed`, err);
		});

	}, [restaurant?.id, tables, events, queryClient]);
}
